// protocollo.ts — messaggi del canale in tempo reale (WebSocket /ws), condivisi tra app e API.
// Ogni messaggio è JSON con un campo "tipo". Tutto ciò che arriva dal telefono si controlla qui, senza librerie:
// un messaggio non valido si scarta (e il server risponde "messaggio_non_valido").

import type { TipoSerie, VistaPartita } from "./partita.ts";
import { POSTI_GRUPPO, ePosto, type Posto, type VistaGruppo } from "./gruppo.ts";

export const VERSIONE_PROTOCOLLO = 3;

/** Dimensione massima di un messaggio dal telefono al server, in byte (una descrizione WebRTC ci sta). */
export const MESSAGGIO_MAX_BYTE = 16_384;
/** Lunghezza massima di una descrizione di sessione WebRTC (SDP) e di un candidato ICE. */
export const SDP_MAX = 12_000;
export const CANDIDATO_MAX = 1_000;
/** Nome visibile di un ospite. */
export const NOME_MIN = 1;
export const NOME_MAX = 24;
/** Codice della sfida (nel link /c/<codice>) e token del giocatore. */
export const CODICE_REGEX = /^[A-Za-z0-9_-]{10,40}$/;
export const TOKEN_GIOCATORE_REGEX = /^[A-Za-z0-9_-]{43}$/;
/** Identificativo casuale del dispositivo, creato dall'app e salvato sul telefono (debole: si perde reinstallando). */
/** Coppia di contatori dei collegamenti nei segnali della partita a 4 («3-7»). */
export const SESSIONE_REGEX = /^[0-9]{1,9}-[0-9]{1,9}$/;
export const DISPOSITIVO_REGEX = /^[A-Za-z0-9_-]{22,43}$/;

export type SoggettoRisata = "io" | "avversario";

/**
 * Torneo: chi guarda la finale (gli eliminati) ha un posto "s1".."s4" (il suo posto nel torneo). I segnali WebRTC
 * tra un giocatore e uno spettatore portano il destinatario ("con") e il mittente ("da"); senza, vanno all'avversario.
 */
export const SPETTATORI = ["s1", "s2", "s3", "s4"] as const;
export type Spettatore = (typeof SPETTATORI)[number];
export type Chi = "a" | "b" | Spettatore;
/** Destinatario o mittente di un segnale WebRTC: un giocatore, uno spettatore o un posto della partita a 4 (v013). */
export type Destinatario = Chi | Posto;
const CHI: readonly string[] = ["a", "b", ...SPETTATORI, ...POSTI_GRUPPO];
/** Modalità di prova (v008): pulsanti del tester nella partita contro il bot. */
export const AZIONI_PROVA = [
  "avversario_ride", // il bot ride (riconosciuto dai due telefoni)
  "io_rido", // la mia risata riconosciuta dai due telefoni
  "avversario_accusa", // il bot preme «Ha riso!» contro di me
  "avversario_contesta", // la prossima accusa contro il bot sarà contestata
  "avversario_volto_via", // il bot esce dall'inquadratura
  "avversario_volto_torna",
  "avversario_scollegato", // il bot perde la rete
  "avversario_torna",
  "avversario_esce", // il bot lascia la partita
  "avversario_propone", // il bot propone «al meglio di 3»
] as const;
export type AzioneProva = (typeof AZIONI_PROVA)[number];
/** v039: i pulsanti del tester nella partita a 4 con i bot. */
export const AZIONI_PROVA_GRUPPO = [
  "bot_ride", // il bot `su` ride (riconosciuto da due telefoni)
  "io_rido", // ho riso io
  "vinco", // ridono tutti i bot, uno dopo l'altro: vinco io (per provare la fine e la condivisione)
] as const;
export type AzioneProvaGruppo = (typeof AZIONI_PROVA_GRUPPO)[number];

export const eSpettatore = (c: string): c is Spettatore => (SPETTATORI as readonly string[]).includes(c);

/** Dati WebRTC passati da un telefono all'altro attraverso il server (il server non li legge). */
export type DatiSegnale =
  | { descrizione: { type: "offer" | "answer"; sdp: string } }
  | { candidato: { candidate: string; sdpMid: string | null; sdpMLineIndex: number | null; usernameFragment: string | null } | null };

export type MessaggioClient =
  | { tipo: "ping"; id: number; t0: number }
  | { tipo: "entra"; codice: string; nome: string; token: string | null; dispositivo: string }
  | { tipo: "pronto"; valore: boolean }
  /** `sessione` (partita a 4): la coppia di collegamenti di chi manda, «mia-tua»; i segnali di una coppia vecchia si scartano. */
  | { tipo: "segnale"; dati: DatiSegnale; con?: Destinatario; sessione?: string }
  | { tipo: "guarda"; codice: string; token: string; dispositivo: string }
  /** Partita a 4 (v013): entra con il token avuto dal link /g/<codice>. */
  | { tipo: "entra_gruppo"; codice: string; token: string; dispositivo: string }
  /** Partita a 4: il via (solo chi organizza, o il primo collegato se lui non c'è). */
  | { tipo: "avvia" }
  /** Consenso alla clip della risata (vale per la stanza; la clip si fa solo se lo danno tutti e due). */
  | { tipo: "clip"; consenso: boolean }
  /** v037: sì a mettere online il video in cui si vede (link /v/…). */
  | { tipo: "online"; consenso: boolean }
  /** v045: sì a «video per noi» (il video della risata salvato per i social e il sito di Laughdown; solo se lo dicono tutti e due). */
  | { tipo: "noi"; consenso: boolean }
  /** v040: sì alle foto della partita in cui si vede (senza, nelle foto c'è solo chi ha detto sì). */
  | { tipo: "foto"; consenso: boolean }
  /** v038b: audio spento (chi lo spegne non sente gli altri e gli altri non sentono lui; tutti vedono l'icona). */
  | { tipo: "muto"; valore: boolean }
  /** Solo nella partita di prova contro il bot (tester). */
  | { tipo: "prova"; azione: AzioneProva }
  /** v039: solo nella partita a 4 di prova con i bot (tester). */
  | { tipo: "prova_gruppo"; azione: AzioneProvaGruppo; su?: Posto }
  /** `su`: nella partita a 4, di chi è la risata (sé stesso o il vicino che si guarda). */
  | { tipo: "risata"; soggetto: SoggettoRisata; t: number; su?: Posto }
  /** `contro`: nella partita a 4, chi si accusa. */
  | { tipo: "ha_riso"; contro?: Posto }
  | { tipo: "rispondi"; conferma: boolean }
  | { tipo: "volto"; presente: boolean }
  | { tipo: "proponi"; serie: TipoSerie }
  | { tipo: "accetta" }
  | { tipo: "rifiuta" }
  | { tipo: "esci" };

export type MessaggioServer =
  | { tipo: "benvenuto"; protocollo: number; versione: string; tServer: number }
  | { tipo: "pong"; id: number; t0: number; tServer: number }
  | { tipo: "errore"; codice: CodiceErroreWs }
  | { tipo: "entrato"; codice: string; ruolo: "a" | "b"; token: string; iniziatore: boolean; /** partita di prova contro il bot */ prova?: boolean; /** «Prova da solo» (v015): niente pulsanti del tester */ daSolo?: boolean }
  | { tipo: "partita"; vista: VistaPartita }
  | { tipo: "segnale"; dati: DatiSegnale; da?: Destinatario; sessione?: string }
  /** Partita a 4: sei dentro con questo posto. */
  | { tipo: "entrato_gruppo"; codice: string; posto: Posto }
  | { tipo: "gruppo"; vista: VistaGruppo }
  /** Torneo: sei dentro la finale come spettatore (posto nel torneo). */
  | { tipo: "spettatore"; codice: string; posto: Spettatore }
  /** Ai giocatori: chi sta guardando adesso (per chiudere le connessioni video di chi se n'è andato). */
  | { tipo: "spettatori"; posti: Spettatore[] }
  /** Allo spettatore: la stanza si è chiusa. */
  | { tipo: "guarda_fine" };

export const CODICI_ERRORE_WS = [
  "messaggio_non_valido",
  "troppi_messaggi",
  "protocollo_non_supportato",
  "sfida_non_trovata", // codice sbagliato, scaduto o revocato
  "stanza_piena", // ci sono già due giocatori
  "non_in_stanza", // messaggio di gioco prima di "entra"
  "bloccato", // uno dei due ha bloccato l'altro, o il dispositivo è sospeso
  "sostituito", // lo stesso giocatore è entrato da un'altra scheda o un altro telefono
  "nome_vietato", // il nome contiene una parola vietata (v014): se ne sceglie un altro
  "errore_interno",
] as const;
export type CodiceErroreWs = (typeof CODICI_ERRORE_WS)[number];

const SERIE: readonly TipoSerie[] = ["singola", "meglio3"];

const eOggetto = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const testoBreve = (v: unknown, max: number): v is string => typeof v === "string" && v.length <= max;

/** Nome visibile: spazi ridotti, niente caratteri di controllo; null se non valido. */
export function pulisciNome(v: unknown): string | null {
  if (typeof v !== "string") return null;
  // eslint-disable-next-line no-control-regex
  const n = v.replace(/[\u0000-\u001f\u007f​-‏‪-‮⁦-⁩]/g, "").replace(/\s+/g, " ").trim();
  const lunghezza = [...n].length;
  return lunghezza >= NOME_MIN && lunghezza <= NOME_MAX ? n : null;
}

function leggiSegnale(v: unknown): DatiSegnale | null {
  if (!eOggetto(v)) return null;
  if ("descrizione" in v) {
    const d = v.descrizione;
    if (!eOggetto(d) || (d.type !== "offer" && d.type !== "answer") || !testoBreve(d.sdp, SDP_MAX)) return null;
    return { descrizione: { type: d.type, sdp: d.sdp } };
  }
  if ("candidato" in v) {
    const c = v.candidato;
    if (c === null) return { candidato: null }; // fine dei candidati
    if (!eOggetto(c) || !testoBreve(c.candidate, CANDIDATO_MAX)) return null;
    const mid = c.sdpMid ?? null;
    const indice = c.sdpMLineIndex ?? null;
    const frammento = c.usernameFragment ?? null;
    if (mid !== null && !testoBreve(mid, 64)) return null;
    if (indice !== null && (typeof indice !== "number" || !Number.isInteger(indice) || indice < 0 || indice > 64)) return null;
    if (frammento !== null && !testoBreve(frammento, 256)) return null;
    return { candidato: { candidate: c.candidate, sdpMid: mid, sdpMLineIndex: indice, usernameFragment: frammento } };
  }
  return null;
}

/** Controllo di un messaggio in arrivo dal telefono. */
export function leggiMessaggioClient(testo: string): MessaggioClient | null {
  if (testo.length > MESSAGGIO_MAX_BYTE) return null;
  let dato: unknown;
  try {
    dato = JSON.parse(testo);
  } catch {
    return null;
  }
  if (!eOggetto(dato)) return null;
  const m = dato;
  switch (m.tipo) {
    case "ping":
      if (typeof m.id !== "number" || !Number.isInteger(m.id) || m.id < 0 || m.id > 1e9) return null;
      if (typeof m.t0 !== "number" || !Number.isFinite(m.t0)) return null;
      return { tipo: "ping", id: m.id, t0: m.t0 };
    case "entra": {
      if (typeof m.codice !== "string" || !CODICE_REGEX.test(m.codice)) return null;
      const nome = pulisciNome(m.nome);
      if (!nome) return null;
      const token = m.token ?? null;
      if (token !== null && (typeof token !== "string" || !TOKEN_GIOCATORE_REGEX.test(token))) return null;
      if (typeof m.dispositivo !== "string" || !DISPOSITIVO_REGEX.test(m.dispositivo)) return null;
      return { tipo: "entra", codice: m.codice, nome, token, dispositivo: m.dispositivo };
    }
    case "pronto":
      return typeof m.valore === "boolean" ? { tipo: "pronto", valore: m.valore } : null;
    case "segnale": {
      const dati = leggiSegnale(m.dati);
      if (!dati) return null;
      if (m.sessione !== undefined && (typeof m.sessione !== "string" || !SESSIONE_REGEX.test(m.sessione))) return null;
      const sessione = m.sessione !== undefined ? { sessione: m.sessione as string } : {};
      if (m.con === undefined) return { tipo: "segnale", dati, ...sessione };
      return typeof m.con === "string" && CHI.includes(m.con) ? { tipo: "segnale", dati, con: m.con as Destinatario, ...sessione } : null;
    }
    case "clip":
      return typeof m.consenso === "boolean" ? { tipo: "clip", consenso: m.consenso } : null;
    case "online":
      return typeof m.consenso === "boolean" ? { tipo: "online", consenso: m.consenso } : null;
    case "noi":
      return typeof m.consenso === "boolean" ? { tipo: "noi", consenso: m.consenso } : null;
    case "foto":
      return typeof m.consenso === "boolean" ? { tipo: "foto", consenso: m.consenso } : null;
    case "muto":
      return typeof m.valore === "boolean" ? { tipo: "muto", valore: m.valore } : null;
    case "prova":
      return typeof m.azione === "string" && (AZIONI_PROVA as readonly string[]).includes(m.azione) ? { tipo: "prova", azione: m.azione as AzioneProva } : null;
    case "prova_gruppo":
      if (typeof m.azione !== "string" || !(AZIONI_PROVA_GRUPPO as readonly string[]).includes(m.azione)) return null;
      if (m.su !== undefined && !ePosto(m.su)) return null;
      return { tipo: "prova_gruppo", azione: m.azione as AzioneProvaGruppo, ...(m.su !== undefined ? { su: m.su as Posto } : {}) };
    case "guarda":
      if (typeof m.codice !== "string" || !CODICE_REGEX.test(m.codice)) return null;
      if (typeof m.token !== "string" || !TOKEN_GIOCATORE_REGEX.test(m.token)) return null;
      if (typeof m.dispositivo !== "string" || !DISPOSITIVO_REGEX.test(m.dispositivo)) return null;
      return { tipo: "guarda", codice: m.codice, token: m.token, dispositivo: m.dispositivo };
    case "entra_gruppo":
      if (typeof m.codice !== "string" || !CODICE_REGEX.test(m.codice)) return null;
      if (typeof m.token !== "string" || !TOKEN_GIOCATORE_REGEX.test(m.token)) return null;
      if (typeof m.dispositivo !== "string" || !DISPOSITIVO_REGEX.test(m.dispositivo)) return null;
      return { tipo: "entra_gruppo", codice: m.codice, token: m.token, dispositivo: m.dispositivo };
    case "risata":
      if (m.soggetto !== "io" && m.soggetto !== "avversario") return null;
      if (typeof m.t !== "number" || !Number.isFinite(m.t)) return null;
      if (m.su !== undefined && !ePosto(m.su)) return null;
      return m.su !== undefined ? { tipo: "risata", soggetto: m.soggetto, t: m.t, su: m.su } : { tipo: "risata", soggetto: m.soggetto, t: m.t };
    case "ha_riso":
      if (m.contro === undefined) return { tipo: "ha_riso" };
      return ePosto(m.contro) ? { tipo: "ha_riso", contro: m.contro } : null;
    case "rispondi":
      return typeof m.conferma === "boolean" ? { tipo: "rispondi", conferma: m.conferma } : null;
    case "volto":
      return typeof m.presente === "boolean" ? { tipo: "volto", presente: m.presente } : null;
    case "proponi":
      return SERIE.includes(m.serie as TipoSerie) ? { tipo: "proponi", serie: m.serie as TipoSerie } : null;
    case "avvia":
    case "accetta":
    case "rifiuta":
    case "esci":
      return { tipo: m.tipo };
    default:
      return null;
  }
}
